import { DoubleSide, Mesh, MeshBasicMaterial, PlaneGeometry, type Scene, TextureLoader } from 'three';

type TGobeletBoundingBox = { depth: number; height: number; width: number } | null;

type TGroundShadowServiceParams = {
    getGobeletBoundingBox: () => TGobeletBoundingBox;
    scene: Scene;
};

const SHADOW_MESH_NAME = 'ground-shadow';
const SHADOW_TEXTURE_URL = '/models/textures/gobelet-shadow.webp';
const SHADOW_DEFAULT_SIZE = 0.8;
const SHADOW_FALLBACK_SIZE = 2.0;
const SHADOW_SCALE = 1.15;
const SHADOW_SIZE_MIN = 0.1;
const SHADOW_SIZE_MAX = 20;
const SHADOW_Y_OFFSET = -0.002;
const SHADOW_OPACITY = 0.4;
const FALLBACK_TEXTURE_SIZE_PX = 512;

// Behavior-preserving extraction of the gobelet ground-shadow concern
// previously living in Editor3DWorld (createGround, createShadowImage,
// createFallbackTexture, computeShadowSize, updateShadowSize).
//
// The service is injected with the scene and a getter for the gobelet's
// bounding box (populated by the model-loading flow before the shadow
// is created / updated). It owns the shadow mesh lifecycle end-to-end.
class GroundShadowService {
    private readonly _getGobeletBoundingBox: () => TGobeletBoundingBox;
    private readonly _scene: Scene;

    constructor(params: TGroundShadowServiceParams) {
        this._getGobeletBoundingBox = params.getGobeletBoundingBox;
        this._scene = params.scene;
    }

    // Idempotent: creates the shadow mesh on first call, resizes it on
    // subsequent calls (as the gobelet bounding box becomes known).
    createOrUpdate(): void {
        const existing = this._scene.getObjectByName(SHADOW_MESH_NAME);
        if (!existing) {
            this._createShadowImage();

            return;
        }
        this._updateShadowSize();
    }

    // Programmatic radial-gradient fallback used when the WebP shadow
    // texture fails to load. Same colors / stops as the legacy code.
    private _createFallbackTexture(material: MeshBasicMaterial): void {
        const canvas = document.createElement('canvas');
        canvas.width = FALLBACK_TEXTURE_SIZE_PX;
        canvas.height = FALLBACK_TEXTURE_SIZE_PX;
        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }

        const gradient = context.createRadialGradient(256, 256, 0, 256, 256, 220);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.55)');
        gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.25)');
        gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.08)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

        context.fillStyle = gradient;
        context.fillRect(0, 0, FALLBACK_TEXTURE_SIZE_PX, FALLBACK_TEXTURE_SIZE_PX);

        const fallbackTexture = new TextureLoader().load(canvas.toDataURL());
        material.map = fallbackTexture;
        material.color.setHex(0xffffff);
        material.needsUpdate = true;
    }

    private _createShadowImage(): void {
        const shadowSize = this.computeShadowSize();
        const shadowGeometry = new PlaneGeometry(shadowSize.width, shadowSize.depth);
        const shadowMaterial = new MeshBasicMaterial({
            depthTest: true,
            depthWrite: false,
            opacity: SHADOW_OPACITY,
            side: DoubleSide,
            transparent: true,
        });

        const shadowPlane = new Mesh(shadowGeometry, shadowMaterial);
        shadowPlane.name = SHADOW_MESH_NAME;
        shadowPlane.rotation.x = -Math.PI / 2;
        shadowPlane.position.y = SHADOW_Y_OFFSET;
        shadowPlane.renderOrder = -1;
        // Its transform is set once and never touched again: _updateShadowSize replaces the
        // geometry, not the position or rotation. So the per-frame matrix recomputation buys
        // nothing.
        shadowPlane.matrixAutoUpdate = false;
        shadowPlane.updateMatrix();

        this._scene.add(shadowPlane);

        const textureLoader = new TextureLoader();
        textureLoader.load(
            SHADOW_TEXTURE_URL,
            (texture) => {
                shadowMaterial.map = texture;
                shadowMaterial.color.setHex(0xffffff);
                shadowMaterial.needsUpdate = true;
            },
            undefined,
            () => {
                this._createFallbackTexture(shadowMaterial);
            },
        );
    }

    // Kept exposed via computeShadowSize so tests can assert the math
    // without touching Three.js.
    computeShadowSize(): { depth: number; width: number } {
        const bbox = this._getGobeletBoundingBox();
        if (!bbox) {
            return { depth: SHADOW_DEFAULT_SIZE, width: SHADOW_DEFAULT_SIZE };
        }
        const baseSize = Math.min(bbox.width, bbox.depth);
        const shadowSize = baseSize * SHADOW_SCALE;

        if (shadowSize < SHADOW_SIZE_MIN || shadowSize > SHADOW_SIZE_MAX) {
            console.warn(`Shadow size seems unreasonable: ${shadowSize.toFixed(3)}, using fallback`);

            return { depth: SHADOW_FALLBACK_SIZE, width: SHADOW_FALLBACK_SIZE };
        }

        return { depth: shadowSize, width: shadowSize };
    }

    private _updateShadowSize(): void {
        const existingShadow = this._scene.getObjectByName(SHADOW_MESH_NAME) as Mesh | undefined;
        if (!existingShadow || !this._getGobeletBoundingBox()) {
            if (!existingShadow) {
                console.warn('No shadow found to update');
            }
            return;
        }
        const newSize = this.computeShadowSize();
        const currentGeometry = existingShadow.geometry as PlaneGeometry;
        currentGeometry.dispose();
        existingShadow.geometry = new PlaneGeometry(newSize.width, newSize.depth);
    }
}

export { GroundShadowService };
